import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import en, { TranslationKeys } from './en';
import fr from './fr';
import type { Locale } from '../types';

const translations: Record<Locale, TranslationKeys> = { en, fr };

type NestedKeyOf<T> = T extends string | number | boolean
  ? ''
  : {
      [K in keyof T & string]: T[K] extends string
        ? K
        : `${K}.${NestedKeyOf<T[K]>}`;
    }[keyof T & string];

type FlatKey = NestedKeyOf<TranslationKeys>;

function getNestedValue(obj: Record<string, unknown>, path: string): string {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return path;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : path;
}

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: FlatKey, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue>({
  locale: 'en',
  setLocale: () => {},
  t: (key) => key,
});

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const storedLocale = (typeof localStorage !== 'undefined' ? localStorage.getItem('oasix_locale') : null) as Locale | null;
  const [locale, setLocaleState] = useState<Locale>(storedLocale ?? 'en');

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try { localStorage.setItem('oasix_locale', l); } catch { /* noop */ }
  }, []);

  const t = useCallback(
    (key: FlatKey, params?: Record<string, string | number>): string => {
      let value = getNestedValue(translations[locale] as unknown as Record<string, unknown>, key);
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          value = value.replace(`{${k}}`, String(v));
        });
      }
      return value;
    },
    [locale]
  );

  const contextValue = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return React.createElement(I18nContext.Provider, { value: contextValue }, children);
}

export function useI18n() {
  return useContext(I18nContext);
}

export type { Locale, TranslationKeys, FlatKey };
